import React, { useEffect, useRef, useState } from "react";
import { midiToKey } from "../../lib/keyboardPlan";
import { JAM_KEYS, KEYS, flowTarget } from "../../lib/numberSenseData";
import { Icon, Keys, MidiBadge } from "../numberSenseUi";
import { useMidiHeld } from "./useMidi";

/* ============================================================
   Number Sense — keyboard flow. A MIDI-only rapid drill: the app
   calls a target (a chord by number, or a single scale note) and the
   moment you play it on the keyboard it advances — no buttons, no
   multiple choice. Timed 60s round or endless practice. Pure
   keyboarding fluency.

   Props: onBack : () => void
   ============================================================ */
const ROUND_SECONDS = 60;
const STREAK_MS = 4000; // play within this to keep the streak going
const now = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

const FlowScreen = ({ unlockedKeys = [], onBack }) => {
  const [phase, setPhase] = useState("idle"); // idle | play | over
  const [key, setKey] = useState("C"); // a key id, or "all" to roam unlocked keys
  const [mode, setMode] = useState("chords"); // chords | notes
  const [timed, setTimed] = useState(true);
  const [showNames, setShowNames] = useState(true);
  const [target, setTarget] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ROUND_SECONDS);
  const armedRef = useRef(true); // must release before the next hit counts
  const targetAtRef = useRef(0);

  const midi = useMidiHeld(true);

  // Auto-advance: when the held notes match the call, score it and roll the next.
  useEffect(() => {
    if (phase !== "play" || !target) return;
    if (midi.held.size === 0) {
      armedRef.current = true;
      return;
    }
    if (!armedRef.current) return;
    const pcs = new Set([...midi.held].map((m) => midiToKey(m).name));
    const ok =
      pcs.size === target.notes.length && target.notes.every((n) => pcs.has(n));
    if (!ok) return;
    armedRef.current = false;
    const quick = now() - targetAtRef.current <= STREAK_MS;
    setScore((s) => s + 1);
    setStreak((s) => (quick ? s + 1 : 0));
    targetAtRef.current = now();
    const pool = unlockedKeys.length ? unlockedKeys : KEYS;
    const nextKey = key === "all" ? pool[Math.floor(Math.random() * pool.length)] : key;
    setTarget(flowTarget(nextKey, mode, target.label));
  }, [midi.held, phase, target, key, mode, unlockedKeys]);

  useEffect(() => {
    setBest((b) => Math.max(b, streak));
  }, [streak]);

  // The round clock (timed mode only).
  useEffect(() => {
    if (phase !== "play" || !timed) return undefined;
    if (timeLeft <= 0) {
      setPhase("over");
      return undefined;
    }
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, timed, timeLeft]);

  const start = () => {
    setScore(0);
    setStreak(0);
    setBest(0);
    setTimeLeft(ROUND_SECONDS);
    armedRef.current = true;
    targetAtRef.current = now();
    const pool = unlockedKeys.length ? unlockedKeys : KEYS;
    const k = key === "all" ? pool[Math.floor(Math.random() * pool.length)] : key;
    setTarget(flowTarget(k, mode));
    setPhase("play");
  };

  const bar = (title) => (
    <div className="subbar">
      <button
        className="iconbtn"
        onClick={phase === "play" ? () => setPhase("idle") : onBack}
        aria-label="back"
      >
        <Icon name="back" />
      </button>
      <div className="subbar-title">{title}</div>
    </div>
  );

  if (midi.status === "unsupported") {
    return (
      <div className="ns-root">
        <div className="ns-app">
          {bar("keyboard flow")}
          <div className="scroll">
            <div className="ns-stats-empty">
              <Keys />
              <p>
                Keyboard flow needs the Web MIDI API and a connected MIDI keyboard
                — this browser doesn't support it. Try Chrome on desktop.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- running a round ----
  if (phase === "play") {
    return (
      <div className="ns-root">
        <div className="ns-app">
          {bar("keyboard flow")}
          <div className="scroll">
            <div className="flow-status">
              {timed ? (
                <div className={`flow-clock ${timeLeft <= 5 ? "low" : ""}`}>
                  <Icon name="timer" /> {timeLeft}s
                </div>
              ) : (
                <div className="flow-clock">
                  <Icon name="bolt" /> endless
                </div>
              )}
              <div className="flow-tally">
                <span>{score} played</span>
                <span className="muted">best run {best}</span>
              </div>
            </div>

            <div className="flow-call">
              <div className="flow-key">key of {target ? target.key : key}</div>
              <div className="flow-deg mono">{target ? target.label : ""}</div>
              {showNames && target && <div className="flow-name">{target.name}</div>}
              <div className="flow-hint muted">
                {mode === "notes" ? "play this note" : "play this chord"}
              </div>
            </div>

            <MidiBadge status={midi.status} devices={midi.devices} />

            <div className="jam-transport">
              <button className="btn ghost" onClick={() => setPhase("over")}>
                {timed ? "end now" : "done"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- round over ----
  if (phase === "over") {
    return (
      <div className="ns-root">
        <div className="ns-app">
          {bar("keyboard flow")}
          <div className="scroll">
            <div className="rt-result">
              <div className="rt-score" style={{ color: "var(--teal)" }}>
                {score}
              </div>
              <div className="rt-verdict">
                {mode === "notes" ? "notes" : "chords"} played
                {timed ? ` in ${ROUND_SECONDS}s` : ""}
              </div>
              <div className="ns-set-sub">best run: {best} in a row · key of {key}</div>
            </div>
            <div className="jam-transport">
              <button className="btn" onClick={start}>
                <Icon name="play" /> go again
              </button>
            </div>
            <div className="jam-transport">
              <button className="btn ghost" onClick={() => setPhase("idle")}>
                change setup
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- setup ----
  return (
    <div className="ns-root">
      <div className="ns-app">
        {bar("keyboard flow")}
        <div className="scroll">
          <p className="games-intro">
            A keyboard-only drill: the app calls a {mode === "notes" ? "note" : "chord"},
            you play it, and it jumps to the next the instant you nail it. No
            buttons, no multiple choice — just your hands. Needs a MIDI keyboard.
          </p>

          <div className="jam-section">key</div>
          <div className="jam-pills">
            <button
              className={`jam-pill ${key === "all" ? "on" : ""}`}
              onClick={() => setKey("all")}
            >
              all unlocked
            </button>
            {JAM_KEYS.map((k) => (
              <button
                key={k}
                className={`jam-pill mono ${k === key ? "on" : ""}`}
                onClick={() => setKey(k)}
              >
                {k}
              </button>
            ))}
          </div>
          {key === "all" && (
            <p className="rt-desc">
              {unlockedKeys.length
                ? `roams your unlocked keys: ${unlockedKeys.join(" · ")}`
                : "finish a lesson to unlock keys — until then this roams C · G · D · A"}
            </p>
          )}

          <div className="jam-section">drill</div>
          <div className="jam-pills">
            <button
              className={`jam-pill ${mode === "chords" ? "on" : ""}`}
              onClick={() => setMode("chords")}
            >
              chords
            </button>
            <button
              className={`jam-pill ${mode === "notes" ? "on" : ""}`}
              onClick={() => setMode("notes")}
            >
              notes
            </button>
          </div>

          <div className="jam-section">round</div>
          <div className="jam-pills">
            <button
              className={`jam-pill ${timed ? "on" : ""}`}
              onClick={() => setTimed(true)}
            >
              timed · 60s
            </button>
            <button
              className={`jam-pill ${!timed ? "on" : ""}`}
              onClick={() => setTimed(false)}
            >
              endless
            </button>
          </div>

          <div className="ns-set-row">
            <div className="ns-set-label">
              <div className="ns-set-h">show names</div>
              <div className="ns-set-sub">
                {showNames
                  ? "the chord / note name is shown under the number"
                  : "just the number — drill it cold"}
              </div>
            </div>
            <button
              role="switch"
              aria-checked={showNames}
              aria-label="show names"
              className={`ns-switch ${showNames ? "on" : ""}`}
              onClick={() => setShowNames((s) => !s)}
            >
              <span className="knob" />
            </button>
          </div>

          <MidiBadge status={midi.status} devices={midi.devices} />

          <div className="jam-transport">
            <button className="btn" onClick={start}>
              <Icon name="play" /> start
            </button>
          </div>
          {midi.status !== "connected" && (
            <div className="ns-set-sub" style={{ textAlign: "center" }}>
              connect a MIDI keyboard to play
            </div>
          )}

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowScreen;
